import React from "react";
// MeterDetail left card

// Default
import { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

// Mui
import Card from "@mui/material/Card";
import Typography from "@mui/material/Typography";
import Stack from "@mui/material/Stack";
import Divider from "@mui/material/Divider";
import Chip from "@mui/material/Chip";
import Grid from "@mui/material/Grid2";
import useMediaQuery from "@mui/material/useMediaQuery";
import IconButton from "@mui/material/IconButton";
import Accordion from "@mui/material/Accordion";
import AccordionSummary from "@mui/material/AccordionSummary";
import AccordionDetails from "@mui/material/AccordionDetails";

// Svg
import Number from "../../assets/svg/number";
import Home from "../../assets/svg/home";
import Location from "../../assets/svg/location";
import Calendar from "../../assets/svg/calendar";
import Clock from "../../assets/svg/clock";

// Custom
import CustomSearch from "../search/Search";
import DateAndTimePopover from "../popovers/DateAndTimePopover";
import MapViewPopover from "../popovers/MapViewPopover";

// Image
import Meter from "../../assets/images/electric-meter-line-icon-vector 1.png";

// Utils
import { formatDate, formatMeterSerialNumber } from "../../utils/helper";

// Theme
import theme from "../../theme";

// Redux
import { setMeterId } from "../../redux/features/MeterSlice";
import { useAppDispatch, useAppSelector } from "../../redux/hooks";
import { ChevronDown } from "lucide-react";
import { Button } from "@mui/material";

// Types
interface MeterDetailLeftCardProps {
  handleSearch: () => void;
  chipValue: string;
}

const CustomerDetails = ({
  details,
  label,
}: {
  details: undefined | string;
  label: string;
}) => {
  return details ? (
    <Grid size={4} sx={{ display: "flex", flexDirection: "row", gap: "8px" }}>
      <Typography variant="caption" color="#181B25" fontWeight={400}>
        {label} :
      </Typography>
      <Typography variant="caption" color="#181B25" fontWeight={400}>
        {details}
      </Typography>
    </Grid>
  ) : null;
};

const MeterDetailLeftCard: React.FC<MeterDetailLeftCardProps> = ({
  handleSearch,
  chipValue,
}) => {
  const matches = useMediaQuery(theme.breakpoints.down("lg"));
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [startDate, setStartDate] = useState(new Date());
  const [endDate, setEndDate] = useState(new Date());
  const [timeRange, setTimeRange] = useState<number[]>([0, 1439]);
  const [mapView, setMapView] = useState<HTMLElement | null>(null);
  const dispatch = useAppDispatch();
  const location = useLocation();
  const navigate = useNavigate();

  const {
    meterDetails,
    meterId,
    meterV2Details,
    siteV2Details,
    instantTelemetryV2,
  } = useAppSelector((state) => state.meter);
  const {
    customerDetails,
    customerFullDetails,
    customerId,
    customerV2Details,
  } = useAppSelector((state) => state.customer);

  // a9177a70-11ac-11ef-a48b-f7c03fec7f37
  //2b8c03c0-11af-11ef-a48b-f7c03fec7f37

  const handleDateChange = (dates: [Date, Date]) => {
    const [start, end] = dates;
    setStartDate(start);
    setEndDate(end);
  };

  return (
    <Card sx={{ padding: "12px 19px 23px 19px" }}>
      <Grid container pb={"12.5px"}>
        <Grid size={{ xs: 4, md: 4, lg: 4 }} container>
          <Stack direction={"row"} alignItems={"center"} gap={"6px"}>
            <Typography variant="h6" className="meter_left_card_search_text">
              Meter ID:
            </Typography>
            <CustomSearch
              sx={{ width: "450px" }}
              value={meterId || location?.state}
              onChange={(e) => dispatch(setMeterId(e.target.value))}
              onClick={handleSearch}
            />
          </Stack>
        </Grid>

        <Grid
          size={{ xs: 4, md: 4, lg: 4 }}
          container
          justifyContent={"center"}
          alignItems={"center"}
        >
          <Stack>
            <Typography
              variant="h6"
              className="meter_left_card_consumer_text"
              color="#181B25"
              fontWeight={400}
              fontSize={matches ? "12px" : undefined}
            >
              {customerV2Details?.name ||
                formatMeterSerialNumber(meterDetails?.name)}
            </Typography>
          </Stack>
        </Grid>

        <Grid
          size={{ xs: 4, md: 4, lg: 4 }}
          container
          justifyContent={"flex-end"}
        >
          <Stack direction={"row"} alignItems={"center"} gap={"8px"}>
            <Typography
              variant="h6"
              className="meter_left_card_contact_text"
              color="#181B25"
              textAlign={"right"}
              fontWeight={400}
            >
              Registered Contact Number:
              <Typography
                color="#1280E7"
                variant="caption"
                fontWeight={600}
                letterSpacing={"0.32px"}
              >
                {" "}
                {customerV2Details?.phone ||
                  customerDetails?.registerContactNumber}
              </Typography>
            </Typography>
            <img src={Meter} width={20.56} height={17.47} />
          </Stack>
        </Grid>
      </Grid>

      <Divider
        sx={{
          borderColor: "#CACDD7",
        }}
      />

      <Grid container mt={"18px"} mb={"28px"}>
        <Grid
          size={{ xs: 4, md: 4, lg: 4 }}
          container
          pl={matches ? "5px" : "24px"}
        >
          <Stack
            direction={"row"}
            alignItems={"center"}
            gap={"8px"}
            textAlign={"center"}
          >
            <Typography
              variant="caption"
              color="#181B25"
              fontWeight={400}
              textAlign={"right"}
            >
              Sr. No.:
            </Typography>
            <Number />
            <Typography variant="caption">
              {meterV2Details?.meterNumber ||
                formatMeterSerialNumber(meterDetails?.serialNumber)}
            </Typography>
          </Stack>
        </Grid>

        <Grid
          size={{ xs: 4, md: 4, lg: 4 }}
          container
          justifyContent={"center"}
        >
          <Stack
            direction={"row"}
            alignItems={"center"}
            gap={"8px"}
            textAlign={"center"}
          >
            <Typography variant="caption" textAlign={"right"}>
              Status:
            </Typography>
            <Chip
              sx={{ color: "#fff !important" }}
              label={meterV2Details?.isActive ? "Active" : "Inactive"}
              className={`consumer_left_card_status_chip ${
                meterV2Details?.isActive
                  ? "consumer_left_card_status_active"
                  : "consumer_left_card_status_disconnected"
              }`}
            />
          </Stack>
        </Grid>

        <Grid
          size={{ xs: 4, md: 4, lg: 4 }}
          container
          justifyContent={"flex-end"}
          pr={"55px"}
        >
          <Stack
            direction={"row"}
            alignItems={"center"}
            gap={"8px"}
            textAlign={"center"}
          >
            <Typography variant="caption" textAlign={"right"}>
              Type:
            </Typography>
            <Home />
            <Typography variant="caption" fontWeight={400}>
              {meterV2Details?.meterType || meterDetails?.meterCommodityType}
            </Typography>
          </Stack>
        </Grid>
      </Grid>

      <Grid container>
        <Grid
          size={{ xs: 4, md: 4, lg: 4 }}
          container
          pl={matches ? "5px" : "14px"}
        >
          <Stack
            direction={"row"}
            alignItems={"center"}
            gap={"8px"}
            textAlign={"center"}
          >
            <Typography variant="caption" textAlign={"right"}>
              Location:
            </Typography>
            <Location />
            <Typography variant="caption">
              {siteV2Details?.name || customerDetails?.location}
              <Typography
                color="#017EF4"
                variant="caption"
                fontSize={"11px"}
                onClick={(e) => setMapView(e.currentTarget)}
              >
                {" "}
                &nbsp;View
              </Typography>
            </Typography>
          </Stack>
        </Grid>

        <Grid
          size={{ xs: 4, md: 4, lg: 4 }}
          container
          justifyContent={"center"}
        >
          <Stack
            direction={"row"}
            alignItems={"center"}
            gap={"8px"}
            textAlign={"center"}
          >
            <Typography variant="caption" textAlign={"right"}>
              Connected:
            </Typography>
            <Chip
              sx={{ color: "#fff !important" }}
              label={meterV2Details?.isConnected ? "Yes" : "No"}
              className={`consumer_left_card_status_chip ${
                meterV2Details?.isConnected
                  ? "consumer_left_card_status_active"
                  : "consumer_left_card_status_disconnected"
              }`}
            />
          </Stack>
        </Grid>

        <Grid
          size={{ xs: 4, md: 4, lg: 4 }}
          container
          justifyContent={"flex-end"}
        >
          <Stack
            direction={"row"}
            alignItems={"center"}
            gap={"8px"}
            textAlign={"center"}
          >
            <Typography variant="caption" textAlign={"right"}>
              Last Updated At:
            </Typography>
            <Clock />
            <Typography variant="caption">
              {formatDate(
                instantTelemetryV2?.readingTime ||
                  meterV2Details?.updatedAt ||
                  new Date(),
                "DD/MM/YYYY HH:mm:ss",
              )}
            </Typography>
          </Stack>
        </Grid>
      </Grid>

      <Divider
        sx={{
          borderColor: "#CACDD7",
          mt: "12px",
        }}
      />

      {customerId ? (
        <Accordion sx={{ boxShadow: "none" }}>
          <AccordionSummary
            expandIcon={<ChevronDown />}
            aria-controls="panel1-content"
            id="panel1-header"
          >
            <Stack direction={"row"} alignItems={"center"} gap={"8px"}>
              <Typography color="#017EF4" component="span">
                Consumer Name:
              </Typography>
              <Typography component="span">
                {customerV2Details?.name || customerDetails?.name} (
                {customerV2Details?.phone ||
                  customerDetails?.registerContactNumber}
                )
              </Typography>
            </Stack>
          </AccordionSummary>
          <AccordionDetails>
            <Grid container>
              {customerFullDetails?.id?.id && (
                <Grid
                  size={4}
                  sx={{
                    display: "flex",
                    flexDirection: "row",
                    gap: "8px",
                    alignItems: "center",
                  }}
                >
                  <Typography
                    variant="caption"
                    color="#181B25"
                    fontWeight={400}
                  >
                    ID :
                  </Typography>
                  <Typography
                    variant="caption"
                    color="#181B25"
                    fontWeight={400}
                  >
                    {customerFullDetails?.id?.id}
                  </Typography>
                  <Button
                    variant="text"
                    color="primary"
                    sx={{
                      textTransform: "none",
                      fontSize: "12px",
                      fontWeight: 400,
                      color: "#017EF4",
                      textDecoration: "underline",
                      textUnderlineOffset: "2px",
                      textDecorationColor: "#017EF4",
                      textDecorationThickness: "1px",
                      height: "20px",
                    }}
                    onClick={() =>
                      navigate(`/consumer-360`, {
                        state: customerFullDetails?.id?.id,
                      })
                    }
                  >
                    View Counsumer 360
                  </Button>
                </Grid>
              )}
              <CustomerDetails
                details={customerFullDetails?.title}
                label="Title"
              />
              <CustomerDetails
                details={customerV2Details?.email || customerFullDetails?.email}
                label="Email"
              />
              <CustomerDetails
                details={customerV2Details?.phone || customerFullDetails?.phone}
                label="Phone"
              />
              <CustomerDetails
                details={
                  customerV2Details?.address || customerFullDetails?.address
                }
                label="Address"
              />
            </Grid>
          </AccordionDetails>
        </Accordion>
      ) : (
        <Stack
          sx={{
            display: "flex",
            flexDirection: "row",
            gap: "8px",
            alignItems: "center",
            justifyContent: "center",
            height: "50px",
          }}
        >
          <Typography variant="h5" color="#181B25" fontWeight={600}>
            No consumer details found
          </Typography>
        </Stack>
      )}

      <DateAndTimePopover
        anchorEl={anchorEl}
        setAnchorEl={setAnchorEl}
        startDate={startDate}
        endDate={endDate}
        timeRange={timeRange}
        setTimeRange={(timeRange) =>
          setTimeRange(
            Array.isArray(timeRange) ? timeRange : [timeRange, timeRange],
          )
        }
        handleDateChange={handleDateChange}
        handleDateSubmit={() => {}}
      />

      <MapViewPopover mapView={mapView} setMapView={setMapView} />
    </Card>
  );
};

export default MeterDetailLeftCard;
